import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Axios from 'axios';
import moment from 'moment';

import { CanRead } from '@/shared/components/security';
import AuthService from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import { ScrollableTable, ColumnHeader } from '../../shared/components/table';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { getRegistrationModalPropsForTask } from '@/shared/components/registration/registrationHelpers';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import SyncOverviewFilters, { defaultFilters } from './SyncOverviewFilters';
import SyncOverviewRow from './SyncOverviewRow';
import api from '../../shared/api';
import config from '../../config';
import messages from './jobMessages';
import YesNoModal from '@/shared/components/YesNoModal';

const SessionStorageKey = 'SyncOverviewParams';
class SyncOverviewContainer extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);
    const { overviewFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};
    if (sessionFilters.dateFrom) {
      sessionFilters.dateFrom = moment.utc(sessionFilters.dateFrom);
    }
    if (sessionFilters.dateUntil) {
      sessionFilters.dateUntil = moment.utc(sessionFilters.dateUntil);
    }
    this.defaultFiltersWithUserId = {
      ...defaultFilters,
      userId: AuthService.isSupport() ? null : AuthService.getUserId(),
    };

    this.state = {
      items: [],
      currentSearchParams: {},
      initialSearchParams: {
        sortKey: 'createdAt',
        desc: true,
        pageSize: 50,
        overviewFilters: {
          ...this.defaultFiltersWithUserId,
          ...sessionFilters,
        },
        ...sessionParams,
      },
      showRegistrationModal: false,
      activeRegistration: null,
      registrationOptions: {},
      relationMap: {},
      showDeleteConfirmModal: false,
      itemToDelete: null,
    };
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        page: 0,
        overviewFilters: filters,
      },
    }));
  };

  resetFilters = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        page: 0,
        overviewFilters: { ...this.defaultFiltersWithUserId },
        filter: {},
        // forces refresh
        random: new Date().getTime(),
      },
    }));
  };

  onOpenRegistrationModal = item => {
    const { intl } = this.props;

    if (!item.entityId) {
      ToastrService.error(intl.formatMessage(messages.Registration) + ' not found');
      return;
    }

    // Fetch the registration data from API
    const endpoint = `${config.appUrls.api}/api/Registration/${item.entityId}`;

    api.base
      .get(endpoint)
      .then(({ data }) => {
        this.setState({
          showRegistrationModal: true,
          activeRegistration: data,
          registrationOptions: {}, // Add any specific options if needed
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  onRegistrationModalClose = () => {
    this.setState({
      showRegistrationModal: false,
      activeRegistration: null,
      registrationOptions: {},
    });
  };

  onRegistrationSaved = savedRegistration => {
    this.onRegistrationModalClose();
  };

  onDeleteSyncResult = item => {
    this.setState({
      showDeleteConfirmModal: true,
      itemToDelete: item,
    });
  };

  onCancelDelete = () => {
    this.setState({
      showDeleteConfirmModal: false,
      itemToDelete: null,
    });
  };

  onConfirmDelete = () => {
    const { intl } = this.props;
    const { itemToDelete, items } = this.state;

    if (!itemToDelete) return;

    const endpoint = `${config.appUrls.connectApi}/api/syncresult/${itemToDelete.id}`;

    api.base
      .delete(endpoint)
      .then(() => {
        ToastrService.success(intl.formatMessage(messages.SyncResultDeleted));
        this.setState(state => ({
          items: state.items.filter(item => item.id !== itemToDelete.id),
          showDeleteConfirmModal: false,
          itemToDelete: null,
        }));
      })
      .catch(error => {
        handleHttpError(intl)(error);
        this.setState({
          showDeleteConfirmModal: false,
          itemToDelete: null,
        });
      });
  };

  fetchRelationNames = relationIds => {
    const { intl } = this.props;

    if (!relationIds || relationIds.length === 0) {
      return Promise.resolve([]);
    }

    const endpoint = `${config.appUrls.api}/api/relation/names`;
    return api.base
      .post(endpoint, relationIds)
      .then(({ data }) => data)
      .catch(error => {
        handleHttpError(intl)(error);
        return [];
      });
  };

  loadItems = searchParams => {
    sessionStorage.setItem(
      SessionStorageKey,
      JSON.stringify({
        overviewFilters: searchParams.overviewFilters,
        filter: searchParams.filter,
      }),
    );
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    const filter = JSON.stringify(searchParams.filter || {});
    const endpoint = `${config.appUrls.connectApi}/api/syncoverview`;

    return api.base
      .post(endpoint, searchParams.overviewFilters, {
        params: {
          page: searchParams.page,
          filter,
        },
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        const newRelationIds = [...new Set(data.map(item => item.relationId).filter(Boolean))];
        const missingRelationIds = newRelationIds.filter(id => !this.state.relationMap[id]);

        return this.fetchRelationNames(missingRelationIds).then(relations => {
          const newRelationMap = relations.reduce((acc, relation) => {
            acc[relation.id] = relation;
            return acc;
          }, {});

          this.setState(state => ({
            ...state,
            items: searchParams.page > 0 ? [...state.items, ...data] : [...data],
            currentSearchParams: searchParams,
            relationMap: { ...state.relationMap, ...newRelationMap },
          }));

          return data;
        });
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        return [];
      });
  };

  render() {
    const { intl } = this.props;
    const {
      initialSearchParams,
      currentSearchParams,
      items,
      showRegistrationModal,
      activeRegistration,
      registrationOptions,
      relationMap,
      showDeleteConfirmModal,
    } = this.state;
    const hasColumnFilters =
      !!currentSearchParams &&
      !!currentSearchParams.filter &&
      Object.keys(currentSearchParams.filter).length !== 0;

    return (
      <>
        {showRegistrationModal && (
          <RegistrationModal
            registration={activeRegistration}
            registrationOptions={registrationOptions}
            show={showRegistrationModal}
            onSave={this.onRegistrationSaved}
            onClose={this.onRegistrationModalClose}
            okLabel={intl.formatMessage(messages.Save)}
          />
        )}
        {showDeleteConfirmModal && (
          <YesNoModal
            show={showDeleteConfirmModal}
            message={intl.formatMessage(messages.DeleteSyncResultConfirm)}
            onSave={this.onConfirmDelete}
            onClose={this.onCancelDelete}
            onNo={this.onCancelDelete}
          />
        )}
        <CanRead on="settings">
          <SyncOverviewFilters
            filters={initialSearchParams.overviewFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            onResetFilters={this.resetFilters}
            hasColumnFilters={hasColumnFilters}
          />
        </CanRead>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <SyncOverviewRow
              key={item.id}
              item={item}
              intl={intl}
              onOpenRegistrationModal={this.onOpenRegistrationModal}
              onDelete={this.onDeleteSyncResult}
              relation={relationMap[item.relationId]}
            />
          )}
          noDataLabel={<FormattedMessage {...messages.NoJobsFound} />}
        >
          <ColumnHeader title={intl.formatMessage(messages.ConnectPlatform)} />
          <ColumnHeader title={intl.formatMessage(messages.SyncType)} />
          <ColumnHeader title={intl.formatMessage(messages.CreatedBy)} />
          <ColumnHeader title={intl.formatMessage(messages.CreatedAt)} />
          <ColumnHeader title={intl.formatMessage(messages.Entity)} />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} />
          <ColumnHeader title={intl.formatMessage(messages.Errors)} />
          <ColumnHeader title="" />
        </ScrollableTable>
      </>
    );
  }
}

export default injectIntl(SyncOverviewContainer);
